/** Admin → Financial years. NYMR runs March–February; three future years exist by default. */
import { useCallback, useEffect, useState } from "react";
import { addFinancialYear, getYears, setYearStatus, type FinancialYear } from "../../payroll/api";

const STATUSES = ["closed", "current", "future"];

export default function FinancialYearsTab() {
  const [years, setYears] = useState<FinancialYear[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setYears(await getYears()); } catch (e) { setError(msg(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function change(fyCode: string, status: string) {
    setError(null);
    try {
      await setYearStatus(fyCode, status);
      setNotice(`${fyCode} set to ${status}.`);
      await load();
    } catch (e) { setError(msg(e)); }
  }

  async function add() {
    setError(null);
    try {
      const { fyCode } = await addFinancialYear();
      setNotice(`${fyCode} created with 12 periods.`);
      await load();
    } catch (e) { setError(msg(e)); }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Financial years</h2>
          <p className="panel__sub">
            NYMR runs March to February. Period codes are the financial-year end year plus the
            period number (March = 01). Keep three future years ahead so payroll can always be filed.
          </p>
        </div>
        <button className="btn" onClick={() => void add()}>Add financial year</button>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {years === null ? <p className="muted">Loading…</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Financial year</th><th>Periods</th><th>Period codes</th><th>Months filed</th><th>Status</th></tr>
            </thead>
            <tbody>
              {years.map((y) => {
                const filed = y.months.filter((m) => m.status !== "not_started").length;
                return (
                  <tr key={y.fyCode}>
                    <td className="strong">{y.fyCode}</td>
                    <td>{y.months[0]?.monthLabel} – {y.months[y.months.length - 1]?.monthLabel}</td>
                    <td className="mono">{y.months[0]?.periodCode} – {y.months[y.months.length - 1]?.periodCode}</td>
                    <td>{filed} of {y.months.length}</td>
                    <td>
                      <select className="select" value={y.status} onChange={(e) => void change(y.fyCode, e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted">
        Closing a year hides its pills from Payroll Submissions but keeps the history and audit trail.
        Only one year should be “current”.
      </p>
    </section>
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
